import { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { useDebounce } from "@/v2/shared/hooks/useDebounce";
import {
  Flex,
  Text,
  Button,
  TextField,
  Checkbox,
  ScrollArea,
} from "@radix-ui/themes";
import { MagnifyingGlassIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import {
  ReleaseConversation,
  ReleaseConversationFeedback,
} from "@/v2/entities/release-conversation/ReleaseConversation.model";
import {
  useReleaseConversations,
  useSearchReleaseConversations,
  useBatchUpdateReleaseConversationFeedback,
} from "@/v2/entities/release-conversation/ReleaseConversation.api";
import { useGetTopics } from "@/api/useGetTopics";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/dataTabs/tabs";
import { FilterMenu } from "@/v2/components/filter-menu/FilterMenu";
import { FilterMenuType } from "@/v2/components/filter-menu/FilterMenu.model";
import {
  FieldFilter,
  isInFilter,
  isEqualFilter,
} from "@/v2/components/filter-menu/FieldFilter.model";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import Dialog from "@/components/shared/dialog/Dialog";
import { TrainingPanelInteractionCard } from "@/v2/features/simulator/parts/topicsList/TrainingPanelInteractionCard";
import { ExploreInteractionRow } from "@/v2/entities/explore/Explore.model";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { ReleaseConversationFilters } from "@/v2/entities/release-conversation/ReleaseConversation.model";
import styles from "./ConversationsSidebar.module.scss";

interface ConversationsSidebarProps {
  selectedConversationId: number | null;
  onConversationSelect: (conversation: ReleaseConversation) => void;
  onClearSelection: () => void;
  onConversationDataChange: (conversation: ReleaseConversation | null) => void;
  searchPhrase: string;
  onSearchPhraseChange: (value: string) => void;
  semanticSearchPhrase: string;
  onSemanticSearchPhraseChange: (value: string) => void;
  feedbackType: ReleaseConversationFeedback;
  onFeedbackTypeChange: (value: string) => void;
  topicIds: number[];
  onTopicIdsChange: (ids: number[]) => void;
  quackUserId: string | undefined;
  onQuackUserIdChange: (userId: string | undefined) => void;
  page: number;
  onShowMore: () => void;
  selectedIds: number[];
  onSelectedIdsChange: (ids: number[]) => void;
}

const LIMIT = 20;

type FiltersValue = {
  topic?: FieldFilter;
  quackUserId?: FieldFilter;
};

export function ConversationsSidebar({
  selectedConversationId,
  onConversationSelect,
  onClearSelection,
  searchPhrase,
  onSearchPhraseChange,
  semanticSearchPhrase,
  onSemanticSearchPhraseChange,
  feedbackType,
  onFeedbackTypeChange,
  topicIds,
  onTopicIdsChange,
  quackUserId,
  onQuackUserIdChange,
  page,
  onShowMore,
  selectedIds,
  onSelectedIdsChange,
  onConversationDataChange,
}: ConversationsSidebarProps) {
  const { data: topics } = useGetTopics();
  const [isSemanticSearch, setIsSemanticSearch] = useState(false);
  const feedbackToggleEnabled = useFlag(
    "goodResponsesLibraryFeedbackToggle",
    false
  );

  useEffect(() => {
    if (semanticSearchPhrase.trim().length > 0 && !isSemanticSearch) {
      setIsSemanticSearch(true);
    }
  }, [semanticSearchPhrase, isSemanticSearch]);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFeedback, setPendingFeedback] =
    useState<ReleaseConversationFeedback | null>(null);
  const previousConversationsRef = useRef<ReleaseConversation[]>([]);
  const previousPaginationRef = useRef<{
    page: number;
    totalPages: number;
  } | null>(null);

  const filtersValue = useMemo<FiltersValue>(() => {
    const value: FiltersValue = {};

    if (topicIds.length > 0) {
      value.topic = {
        operator: "in",
        value: topicIds.map((id) => id.toString()),
      };
    }

    if (quackUserId) {
      value.quackUserId = {
        operator: "eq",
        value: quackUserId,
      };
    }

    return value;
  }, [topicIds, quackUserId]);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<FiltersValue>) => {
      const topicFilter = newFilters.topic;
      if (topicFilter !== undefined) {
        if (topicFilter && isInFilter(topicFilter)) {
          onTopicIdsChange(topicFilter.value.map((v) => parseInt(v, 10)));
        } else {
          onTopicIdsChange([]);
        }
      } else {
        if (filtersValue.topic) {
          onTopicIdsChange([]);
        }
      }

      const quackUserFilter = newFilters.quackUserId;
      if (quackUserFilter !== undefined) {
        if (quackUserFilter && isEqualFilter(quackUserFilter)) {
          if (typeof quackUserFilter.value === "string") {
            onQuackUserIdChange(quackUserFilter.value);
          } else {
            onQuackUserIdChange(undefined);
          }
        } else {
          onQuackUserIdChange(undefined);
        }
      } else {
        if (filtersValue.quackUserId) {
          onQuackUserIdChange(undefined);
        }
      }
    },
    [filtersValue, onTopicIdsChange, onQuackUserIdChange]
  );

  const filters = useMemo((): ReleaseConversationFilters => {
    const baseFilters: ReleaseConversationFilters = {
      page: 1,
      limit: page * LIMIT,
      feedbackType: [feedbackType],
    };

    if (topicIds.length > 0) {
      baseFilters.topicIds = topicIds;
    }
    if (quackUserId) {
      baseFilters.quackUserId = quackUserId;
    }

    return baseFilters;
  }, [feedbackType, topicIds, quackUserId, page]);

  const debouncedSearchPhrase = useDebounce(searchPhrase, 500);
  const debouncedSemanticSearchPhrase = useDebounce(semanticSearchPhrase, 500);

  const hasSearch =
    debouncedSearchPhrase.trim().length > 0 ||
    debouncedSemanticSearchPhrase.trim().length > 0;
  const searchDto = useMemo(() => {
    if (!hasSearch) return null;
    return {
      search: debouncedSearchPhrase.trim() || undefined,
      semanticSearch: debouncedSemanticSearchPhrase.trim() || undefined,
      page: 1,
      limit: page * LIMIT,
    };
  }, [debouncedSearchPhrase, debouncedSemanticSearchPhrase, page, hasSearch]);

  const { data: conversationsData, isLoading } = useReleaseConversations(
    hasSearch ? {} : filters
  );
  const { data: searchData, isLoading: isSearchLoading } =
    useSearchReleaseConversations(searchDto || {}, hasSearch);

  const conversations = useMemo(() => {
    if (hasSearch) {
      return searchData?.conversations || [];
    }
    return conversationsData?.conversations || [];
  }, [hasSearch, searchData, conversationsData]);

  const pagination = useMemo(() => {
    if (hasSearch) {
      return searchData?.pagination;
    }
    return conversationsData?.pagination;
  }, [hasSearch, searchData, conversationsData]);

  const isLoadingData = isLoading || isSearchLoading;

  // Use previous pagination when loading more and current pagination is unavailable
  const displayPagination = useMemo(() => {
    // When loading more (page > 1) and pagination is temporarily unavailable, use previous
    if (
      isLoadingData &&
      page > 1 &&
      !pagination &&
      previousPaginationRef.current
    ) {
      return previousPaginationRef.current;
    }
    // If we have current pagination, use it
    if (pagination) {
      return pagination;
    }
    // Fallback to previous pagination if available
    return previousPaginationRef.current;
  }, [isLoadingData, page, pagination]);

  // Check if there are more pages using current page state and pagination totalPages
  const hasMore = useMemo(() => {
    if (!displayPagination) {
      // If we're loading more (page > 1) and have previous pagination,
      // keep button visible if previous page suggested there were more pages
      if (isLoadingData && page > 1 && previousPaginationRef.current) {
        const prevPage = previousPaginationRef.current.page;
        const totalPages = previousPaginationRef.current.totalPages;
        // If previous page was less than totalPages, there might be more
        // Keep button visible during loading to avoid flickering
        return prevPage < totalPages;
      }
      return false;
    }
    // Use current page state (which reflects what we're requesting) vs totalPages
    return page < displayPagination.totalPages;
  }, [displayPagination, page, isLoadingData]);

  // Reset previous conversations and pagination when filters change (not when page changes)
  useEffect(() => {
    previousConversationsRef.current = [];
    previousPaginationRef.current = null;
  }, [searchPhrase, semanticSearchPhrase, feedbackType, topicIds, quackUserId]);

  // Track previous conversations and pagination to show them while loading more
  useEffect(() => {
    if (conversations.length > 0) {
      previousConversationsRef.current = conversations;
    }
    if (pagination) {
      previousPaginationRef.current = {
        page: pagination.page,
        totalPages: pagination.totalPages,
      };
    }
  }, [conversations, pagination]);

  // Use previous conversations when loading more and current data is empty
  const displayConversations = useMemo(() => {
    // When loading more (page > 1), show previous conversations if current data is empty
    if (
      isLoadingData &&
      page > 1 &&
      conversations.length === 0 &&
      previousConversationsRef.current.length > 0
    ) {
      return previousConversationsRef.current;
    }
    return conversations;
  }, [isLoadingData, page, conversations]);

  // Only show full loading state on initial load (page 1 and no data yet)
  // When loading more (page > 1), show existing data with loading on button only
  const isInitialLoading =
    isLoadingData && page === 1 && displayConversations.length === 0;
  const isLoadingMore =
    isLoadingData && (page > 1 || displayConversations.length > 0);

  const quackUserOptions = useMemo(() => {
    const userIds = new Set<string>();
    conversations.forEach((conv) => {
      if (conv.quackUserId) {
        userIds.add(conv.quackUserId);
      }
    });
    return Array.from(userIds).sort();
  }, [conversations]);

  const filterMenu = useMemo((): FilterMenuType<keyof FiltersValue> => {
    const menu: FilterMenuType<keyof FiltersValue> = [];

    if (topics && topics.length > 0) {
      const topicsArray = Array.isArray(topics) ? topics : [];
      menu.push({
        field: "topic",
        kind: "multi",
        label: "Topic",
        options: topicsArray.map((topic: Topic) => ({
          label: topic.topic,
          value: topic.id.toString(),
        })),
      });
    }

    if (quackUserOptions.length > 0) {
      menu.push({
        field: "quackUserId",
        kind: quackUserOptions.length > 100 ? "search" : "multi",
        label: "Quack User",
        options: quackUserOptions.map((userId) => ({
          label: userId,
          value: userId,
        })),
      });
    }

    return menu;
  }, [topics, quackUserOptions]);

  const { mutate: bulkUpdate, isLoading: isBulkUpdating } =
    useBatchUpdateReleaseConversationFeedback();

  const handleBulkActionClick = useCallback(
    (newFeedback: ReleaseConversationFeedback) => {
      if (selectedIds.length === 0) {
        toast.error("Please select at least one conversation");
        return;
      }
      setPendingFeedback(newFeedback);
      setConfirmDialogOpen(true);
    },
    [selectedIds]
  );

  const handleBulkActionConfirm = useCallback(() => {
    if (!pendingFeedback || selectedIds.length === 0) {
      return;
    }

    bulkUpdate(
      {
        ids: selectedIds,
        feedback: pendingFeedback,
      },
      {
        onSuccess: () => {
          toast.success(
            `${selectedIds.length} conversation${
              selectedIds.length > 1 ? "s" : ""
            } updated successfully`
          );
          onSelectedIdsChange([]);
          setConfirmDialogOpen(false);
          setPendingFeedback(null);
        },
        onError: () => {
          toast.error("Failed to update conversations");
          setConfirmDialogOpen(false);
          setPendingFeedback(null);
        },
      }
    );
  }, [selectedIds, bulkUpdate, onSelectedIdsChange, pendingFeedback]);

  const handleBulkActionCancel = useCallback(() => {
    setConfirmDialogOpen(false);
    setPendingFeedback(null);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.length === displayConversations.length) {
      onSelectedIdsChange([]);
    } else {
      onSelectedIdsChange(displayConversations.map((c) => c.id));
    }
  }, [displayConversations, selectedIds.length, onSelectedIdsChange]);

  const handleToggleSelect = useCallback(
    (id: number) => {
      if (selectedIds.includes(id)) {
        onSelectedIdsChange(selectedIds.filter((i) => i !== id));
      } else {
        onSelectedIdsChange([...selectedIds, id]);
      }
    },
    [selectedIds, onSelectedIdsChange]
  );

  const convertToExploreInteractionRow = useCallback(
    (conversation: ReleaseConversation): ExploreInteractionRow => {
      const topicsArray = Array.isArray(topics) ? topics : [];
      const topic = topicsArray.find(
        (t: Topic) => t.id === conversation.topicId
      );
      const firstComment = conversation.releaseComments?.find(
        (c) => !c.isAgent
      );

      return {
        interactionType: "conversations",
        id: conversation.id.toString(),
        displayId: conversation.id.toString(),
        subject: firstComment?.comment || "No content available",
        description: firstComment?.comment || "No content available",
        createdAt: conversation.createdAt,
        updatedAt: conversation.updatedAt,
        status: "",
        channel: "conversation",
        topic: topic?.topic || "",
        category: conversation.feedback,
        countPublicMessages: conversation.releaseComments?.length || 0,
        countInternalMessages: 0,
      };
    },
    [topics]
  );

  return (
    <Flex direction="column" className={styles.sidebar} p="4" gap="4">
      <Flex direction="column" gap="2">
        <TextField.Root
          placeholder={
            isSemanticSearch
              ? "Search by meaning..."
              : "Search conversations..."
          }
          size="2"
          value={isSemanticSearch ? semanticSearchPhrase : searchPhrase}
          onChange={(e) => {
            if (isSemanticSearch) {
              onSemanticSearchPhraseChange(e.target.value);
            } else {
              onSearchPhraseChange(e.target.value);
            }
          }}
        >
          <TextField.Slot>
            <MagnifyingGlassIcon height="16" width="16" />
          </TextField.Slot>
        </TextField.Root>
        <Flex wrap="wrap" gap="2">
          <Tabs
            value={isSemanticSearch ? "semantic" : "regular"}
            onValueChange={(v) => {
              setIsSemanticSearch(v === "semantic");
              onSearchPhraseChange("");
              onSemanticSearchPhraseChange("");
            }}
          >
            <TabsList size="sm">
              <TabsTrigger value="regular" size="sm">
                Basic
              </TabsTrigger>
              <TabsTrigger value="semantic" size="sm">
                Context
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {feedbackToggleEnabled && (
            <Tabs value={feedbackType} onValueChange={onFeedbackTypeChange}>
              <TabsList size="sm">
                <TabsTrigger value="POSITIVE" size="sm">
                  Positive
                </TabsTrigger>
                <TabsTrigger value="FORGOTTEN" size="sm">
                  Forgotten
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </Flex>
      </Flex>

      <Flex gap="2" align="center">
        <FilterMenu
          size="2"
          menu={filterMenu}
          value={filtersValue}
          onChange={handleFiltersChange}
          label="Filter"
        />
        {selectedIds.length > 0 && feedbackType !== "FORGOTTEN" && (
          <Button
            size="2"
            variant="outline"
            color="red"
            onClick={() => handleBulkActionClick("FORGOTTEN")}
            disabled={isBulkUpdating}
          >
            Bulk Forget
          </Button>
        )}
        {feedbackType !== "POSITIVE" && (
          <Button
            size="2"
            variant="outline"
            color="green"
            onClick={() => handleBulkActionClick("POSITIVE")}
            disabled={isBulkUpdating}
          >
            Bulk Positive
          </Button>
        )}
        <Text size="2" weight="medium">
          {selectedIds.length} selected
        </Text>
      </Flex>

      <Flex
        flexGrow="1"
        direction="column"
        gap="2"
        className={styles.conversationsList}
        overflow="hidden"
      >
        <Flex
          height="100%"
          overflow="hidden"
          style={{ width: "100%", minWidth: 0 }}
        >
          <ScrollArea
            scrollbars="vertical"
            style={{ width: "100%", height: "100%" }}
          >
            <Flex
              p="3"
              height="100%"
              direction="column"
              gap="2"
              style={{ minWidth: 0, width: "100%" }}
            >
              {displayConversations.length > 0 && (
                <Flex align="center" gap="2" className={styles.selectAllRow}>
                  <Checkbox
                    checked={
                      displayConversations.length > 0 &&
                      selectedIds.length === displayConversations.length
                    }
                    onCheckedChange={handleSelectAll}
                  />
                  <Text size="2" color="gray">
                    Select all
                  </Text>
                </Flex>
              )}

              {isInitialLoading ? (
                <Flex justify="center" align="center" p="4">
                  <Text size="2" color="gray">
                    Loading...
                  </Text>
                </Flex>
              ) : displayConversations.length === 0 ? (
                <Flex justify="center" align="center" p="4">
                  <Text size="2" color="gray">
                    No conversations found
                  </Text>
                </Flex>
              ) : (
                <>
                  {displayConversations.map((conversation) => {
                    const exploreInteraction =
                      convertToExploreInteractionRow(conversation);
                    const isSelected =
                      selectedConversationId === conversation.id;
                    const isChecked = selectedIds.includes(conversation.id);

                    return (
                      <Flex
                        key={conversation.id}
                        align="start"
                        gap="2"
                        className={`${styles.conversationItem} ${
                          isSelected ? styles.selected : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          borderRadius: "4px",
                          backgroundColor: isSelected
                            ? "var(--accent-3)"
                            : "transparent",
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() =>
                            handleToggleSelect(conversation.id)
                          }
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginTop: "8px" }}
                        />
                        <Flex
                          style={{ width: "100%", minWidth: 0 }}
                          onClick={() => {
                            onConversationSelect(conversation);
                            onConversationDataChange(conversation);
                          }}
                        >
                          <TrainingPanelInteractionCard
                            interaction={exploreInteraction}
                            interactionType="conversations"
                            onClick={() => {
                              onConversationSelect(conversation);
                              onConversationDataChange(conversation);
                            }}
                          />
                        </Flex>
                      </Flex>
                    );
                  })}

                  {hasMore && (
                    <Flex justify="center" p="2">
                      <Button
                        variant="outline"
                        size="2"
                        onClick={onShowMore}
                        disabled={isLoadingMore}
                      >
                        <Flex gap="2" align="center">
                          {isLoadingMore ? "Loading More" : "Show More"}
                          <ChevronDownIcon />
                        </Flex>
                      </Button>
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </ScrollArea>
        </Flex>
      </Flex>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <Dialog.Content size="small">
          <Dialog.Title>
            {pendingFeedback === "FORGOTTEN"
              ? "Bulk Forget"
              : "Bulk Mark Positive"}
          </Dialog.Title>
          <Flex direction="column" gap="4" p="4">
            <Text size="2">
              Are you sure you want to{" "}
              {pendingFeedback === "FORGOTTEN" ? "forget" : "mark as positive"}{" "}
              {selectedIds.length} conversation
              {selectedIds.length > 1 ? "s" : ""}?
            </Text>
            <Flex gap="3" justify="end" mt="2">
              <Button
                variant="soft"
                color="gray"
                onClick={handleBulkActionCancel}
                disabled={isBulkUpdating}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color={pendingFeedback === "FORGOTTEN" ? "red" : "green"}
                onClick={handleBulkActionConfirm}
                disabled={isBulkUpdating}
              >
                {pendingFeedback === "FORGOTTEN" ? "Forget" : "Mark Positive"}
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </Flex>
  );
}
